import { z } from "zod";

// =========================================================================
// UPSELL CONSTRUCTS SCHEMAS
// =========================================================================

// -------------------------------------------------------------------------
// Esquema para pricing logic
// -------------------------------------------------------------------------

export const PricingLogicSchema = z.discriminatedUnion("action", [
  // No se aplica ningún descuento. El precio es el estándar.
  z.object({
    action: z.literal("none"),
  }),

  // Se establece un precio final fijo por unidad. Ej: "Lleva 2 por $19.99 cada una" */
  z.object({
    action: z.literal("fixed_price_per_item"),
    amount: z.number().nonnegative("El monto debe ser positivo"),
  }),

  // Se aplica un porcentaje de descuento sobre el precio base. Ej: "25% OFF" */
  z.object({
    action: z.literal("percentage_off"),
    value: z
      .number()
      .min(0, "El porcentaje no puede ser negativo")
      .max(100, "El porcentaje no puede ser mayor a 100"),
  }),

  // Se aplica un descuento de un monto fijo. Ej: "$5.000 OFF" */
  z.object({
    action: z.literal("fixed_amount_off"),
    amount: z.number().nonnegative("El monto de descuento debe ser positivo"),
  }),
]);

// -------------------------------------------------------------------------
// Esquemas para display data de las variantes de upsell
// -------------------------------------------------------------------------

const ColorStringSchema = z
  .string()
  .regex(
    /^(#[0-9A-Fa-f]{3,8}|rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*[\d.]+\s*)?\))$/,
    "Debe ser un color HEX o RGBA válido"
  );

// Etiqueta para destacar ofertas
export const BadgeSchema = z
  .object({
    text: z.string(),
    text_color: z.string().optional(),
    background_color: z.string().optional(),
  })
  .nullable();

// Sub-esquema para la configuración visual de un botón
const ButtonStyleSchema = z.object({
  background_color: z.string().optional(),
  text_color: z.string().optional(),
  border_color: z.string().optional(),
  border_width: z.number().int().min(0).optional(),
  border_radius: z.number().int().min(0).optional(),
  text_size: z.number().int().min(10).optional(),
  shadow: z.number().int().min(0).optional(),
  animation: z.enum(["none", "pulse", "shake", "bounce"]).default("none"),
  icon: z.string().optional(),
});

export const ModalDisplaySchema = z.object({
  component_type: z.literal("modal"),

  // -- Textos Superiores --
  title_template: z.string().default("¡Agregar {product_name} a tu pedido!"),
  subtitle_template: z.string().optional(),

  // -- Overrides de Producto --
  // Si están vacíos, el frontend debe usar el fallback del producto original de Shopify
  product_title_override: z.string().optional(),
  description: z.string().optional(),
  image_url: z.url().optional(),

  // -- Cuenta Regresiva --
  countdown_timer: z
    .object({
      enabled: z.boolean().default(false),
      text: z.string().optional(),
      duration_seconds: z.number().int().min(0).optional(), // Suma de H+M+S
    })
    .optional(),

  // -- Botón de Aceptar --
  cta_accept_text: z.string().default("Agregar a tu pedido"),
  cta_accept_styles: ButtonStyleSchema.optional(),

  // -- Botón de Rechazar --
  cta_decline_text: z.string().default("No gracias, completar mi pedido"),
  cta_decline_styles: ButtonStyleSchema.optional(),

  // -- Estilos Generales y Colores --
  styles: z
    .object({
      primary_color: z.string().optional(),
      background_color: z.string().optional(),
      title_color: z.string().optional(),
      title_size: z.number().int().min(10).optional(),
      price_color: z.string().optional(),
      subtitle_color: ColorStringSchema.optional(),
      subtitle_size: z.number().int().min(10).optional(),
      description_color: ColorStringSchema.optional(),
      border_radius: z.number().int().min(0).optional(),
      border_width: z.number().int().min(0).optional(),
      border_color: z.string().optional(),
    })
    .loose()
    .optional(),

  // -- Toggles de Funcionalidad UI --
  enable_quantity_selector: z.boolean().default(false),
  hide_close_icon: z.boolean().default(false),
  hide_variant_selector: z.boolean().default(false),

  // -- Campos exclusivos de downsell (placement: "on_close") --
  // Todos opcionales para no afectar modales de upsell existentes
  background_image_preset: z
    .enum(["preset_1", "preset_2", "preset_3", "none"])
    .optional(),
  times_shown_limit: z.number().int().min(1).optional(),
  discount_badge: z
    .object({
      text: z.string().optional(),
      text_color: z.string().optional(),
      discount_color: z.string().optional(),
      size: z.number().int().min(0).optional(),
    })
    .optional(),
});

// --- Definición para ofertas de cantidad ---
export const QuantityTierSchema = z.object({
  quantity: z.number().int().positive(),
  is_default: z.boolean().default(false),
  title: z.string(),
  hide_compare_at_price: z.boolean().default(false),
  pricing_logic: PricingLogicSchema,
  badge: BadgeSchema,
  styles: z
    .object({
      border_color: z.string().optional(),
      active_border_color: z.string().optional(),
    })
    .loose()
    .optional(),
  price_color: z.string().optional(),
  title_color: z.string().optional(),
  custom_image_url: z.url().optional().nullable(),
});

export const QuantityTiersDisplaySchema = z.object({
  component_type: z.literal("quantity_tiers"),
  layout: z.enum(["horizontal", "grid"]).default("horizontal"),
  tiers: z.array(QuantityTierSchema).min(1, "Debe haber al menos un tier"),
  styles: z
    .object({
      active_border_color: z.string().optional(),
      background_color: z.string().optional(),
      // Background of the currently-selected tier. Independent from
      // `background_color` (the inactive card), so merchants picking a
      // dark inactive background can still keep the selected state
      // visually distinct.
      selected_background_color: z.string().optional(),
      border_radius: z.number().int().min(0).optional(),
    })
    .loose()
    .optional(),
  hide_product_image: z.boolean().default(false),
  hide_variant_quantity_selectors: z.boolean().default(false),
  disable_variant_selectors: z.boolean().default(false),
  use_compare_at_price_as_base: z.boolean().default(false),
  disable_if_exceeds_max_quantity: z.boolean().default(false),
});

// --- Definición para un futuro banner ---
export const BannerDisplaySchema = z.object({
  component_type: z.literal("banner"),
  title: z.string(),
  text: z.string().optional(),
  cta_text: z.string(),
  styles: z
    .object({
      background_image: z.string().optional(),
      background_color: z.string().optional(),
      text_color: z.string().optional(),
    })
    .loose()
    .optional(),
});

export const OneTickDisplaySchema = z.object({
  component_type: z.literal("one_tick"),
  checkbox_text_template: z
    .string()
    .default("Sí, quiero agregar {product_name} a mi pedido por {price}"),
  // Merchant-edited title for the upsell card. Mirrors
  // `product_title_override` on `ModalDisplaySchema` — kept independent
  // from `target.display_meta.title` (which carries the linked product's
  // own title) so the two layers don't conflate.
  product_title_override: z.string().optional(),
  description: z.string().optional(),
  image_url: z.url().optional(),
  text_color: ColorStringSchema.optional(),
  description_color: ColorStringSchema.optional(),
  styles: z
    .object({
      background_color: ColorStringSchema.optional(),
      border_style: z
        .enum(["solid", "dashed", "dotted", "none"])
        .default("solid"),
      border_width: z.number().int().min(0).optional(),
      border_color: ColorStringSchema.optional(),
      border_radius: z.number().int().min(0).optional(),
    })
    .loose()
    .optional(),
});

// ----- Definición para ofertas de bundle -----
// Items para bundle fijo: cantidad definida por el merchant.
// `product_id` is required by the order endpoint's line-item refine —
// each cart line must be either a Shopify line (variant + product) or
// a synthetic line (from_offer_id only). Optional in the schema for
// backwards compatibility with bundles saved before this field
// existed; those bundles need to be re-saved in the admin to capture
// the productId.
const FixedBundleItemSchema = z.object({
  variant_id: z.string(),
  product_id: z.string().optional(),
  quantity: z.number().int().positive(),
  display_meta: z
    .object({
      title: z.string().optional(),
      image_url: z.string().optional(),
      price: z.string().optional(),
    })
    .optional(),
});

// Items para bundle configurable: el cliente elige de un grupo
const ConfigurableBundleGroupSchema = z.object({
  group_id: z.string(),
  name: z.string(),
  pick_count: z.number().int().positive(),
  options: z
    .array(
      z.object({
        variant_id: z.string(),
        // Same rationale as `FixedBundleItemSchema.product_id` —
        // optional for backwards compatibility, required at order
        // submit time.
        product_id: z.string().optional(),
        display_meta: z
          .object({
            title: z.string().optional(),
            image_url: z.string().optional(),
          })
          .optional(),
      })
    )
    .min(1),
});

// items_definition polimórfico, discriminated union desde el día 1
const BundleItemsDefinitionSchema = z.discriminatedUnion("bundle_type", [
  z.object({
    bundle_type: z.literal("fixed"),
    items: z.array(FixedBundleItemSchema).min(1),
  }),
  z.object({
    bundle_type: z.literal("configurable"),
    groups: z.array(ConfigurableBundleGroupSchema).min(1),
  }),
]);

export const BundleDisplaySchema = z.object({
  component_type: z.literal("bundle"),

  // Precios pre-calculados
  base_price: z.number().nonnegative(),
  compare_at_price: z.number().nonnegative().optional(),
  // Explicit flag — merchants who want NO crossed-out price (e.g. when
  // the discount itself is the headline) set this to true. Distinct
  // from `compare_at_price` being absent, which would let consumers
  // fall back to `base_price`. Mirrors QuantityTierSchema's flag.
  hide_compare_at_price: z.boolean().default(false),

  component_placement: z.enum(["embed", "modal"]).default("embed"),

  // Items, discriminados por tipo
  items_definition: BundleItemsDefinitionSchema,

  // Display
  title_override: z.string().optional(),
  description: z.string().optional(),
  image_url: z.url().optional(),

  styles: z
    .object({
      background_color: ColorStringSchema.optional(),
      border_style: z
        .enum(["solid", "dashed", "dotted", "none"])
        .default("solid"),
      border_width: z.number().int().min(0).optional(),
      border_color: ColorStringSchema.optional(),
      border_radius: z.number().int().min(0).optional(),
    })
    .loose()
    .optional(),
});

export const UpsellDisplayDataSchema = z.discriminatedUnion("component_type", [
  ModalDisplaySchema,
  QuantityTiersDisplaySchema,
  BannerDisplaySchema,
  OneTickDisplaySchema,
  BundleDisplaySchema,
  // Futuras variantes aquí
]);

// -------------------------------------------------------------------------
// Esquema para el target del producto (qué producto se ofrece en la oferta)
// -------------------------------------------------------------------------

export const ProductTargetBaseSchema = z.object({
  type: z
    .enum(["shopify_product", "shopify_variant", "bundle", "synthetic"])
    .optional(),
  shopify_product_id: z.string().optional(),
  shopify_variant_id: z.string().optional(),
  bundle_id: z.string().optional(),
  display_meta: z
    .object({
      title: z.string().optional(),
      image_url: z.string().optional(),
      description: z.string().optional(),
      sku: z.string().optional(),
      price: z.string().optional(),
      compare_at_price: z.string().optional(),
      variants: z
        .array(
          z.object({
            id: z.string(),
            title: z.string(),
            image_url: z.string().optional(),
          })
        )
        .optional(),
    })
    .optional(),
});

// --------------------------------------------------------------------------------
// Esquemas para ofertas embeddeds (se comportan como campos dentro del formulario)
// --------------------------------------------------------------------------------

export const OffersEmbedResolvedDataSchema = z.object({
  display_data: UpsellDisplayDataSchema,
  pricing_logic: PricingLogicSchema,
  product_target: ProductTargetBaseSchema,
  trigger_rules: z.record(z.string(), z.any()).optional(),
});

// Esquema para el campo de tipo "offer_embed" dentro de la configuración del formulario
export const OffersEmbedSchema = z.object({
  type: z.literal("offer_embed"),
  key: z.string(), // `upsell_${offer.id}`
  label: z.string(),
  visible: z.boolean(),
  required: z.boolean().optional(),
  display_order: z.number().int(),
  locked: z.literal(true),
  offer_id: z.string(),
  display_type: z.enum(["one_tick", "quantity_tiers", "bundle"]),
  resolved_data: OffersEmbedResolvedDataSchema.optional(), // Este campo no se guarda en DB, solo se resuelve en backend y se envía al frontend
});

export type UpsellDisplayData = z.infer<typeof UpsellDisplayDataSchema>;
export type PricingLogic = z.infer<typeof PricingLogicSchema>;
export type ProductTargetBase = z.infer<typeof ProductTargetBaseSchema>;
export type OffersEmbedResolvedData = z.infer<
  typeof OffersEmbedResolvedDataSchema
>;
